import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PanelRight, X } from 'lucide-react';
import { TooltipProvider } from '../ui/tooltip';
import { Button } from '../ui/button';
import { cn } from '../lib/utils';
import { useIsCompact } from '../hooks/use-media-query';
import { ErrorBoundary } from './error-boundary';
import { ShellPanelProvider } from './shell-panel';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { PageHeaderProvider } from './page-header';
import type {
  AdaptedNavGroup,
  AdaptedNavItem,
  PageHeaderConfig,
  ResolvedBranding,
  RouteTitle,
  SidebarBadge,
} from './types';

interface AppShellProps {
  branding: ResolvedBranding;
  groups: AdaptedNavGroup[];
  head: RouteTitle | PageHeaderConfig;
  /** Optional per-nav-item decorator for badges (e.g. tasks count). */
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  /** Opt-in collapsible nav groups. Default preserves existing portal behavior. */
  collapsibleNavGroups?: boolean;
  navGroupsInitiallyCollapsed?: boolean;
  navGroupStorageKey?: string;
  /** Render desktop navigation sections as right-side page flyouts. */
  subNavigation?: boolean;
  /** Opt-in desktop sidebar collapse. Mobile drawer behavior is unchanged. */
  collapsibleSidebar?: boolean;
  sidebarStorageKey?: string;
  /** Force the desktop sidebar into compact mode without changing persisted preference. */
  forceSidebarCollapsed?: boolean;
  /** Optional app-specific search input rendered in the Header. */
  searchSlot?: ReactNode;
  /** Optional shell-owned command control rendered directly below the brand. */
  sidebarCommand?: ReactNode | ((options: { compact: boolean }) => ReactNode);
  /** Optional organization/workspace/account control rendered below the command control. */
  sidebarWorkspace?: ReactNode | ((options: { compact: boolean }) => ReactNode);
  /** Optional app-specific section rendered under the navigation groups. */
  sidebarExtras?: ReactNode | ((options: { compact: boolean }) => ReactNode);
  /** Optional app-specific action buttons in the Header. */
  actions?: ReactNode;
  headerVariant?: 'compact' | 'workspace';
  /** Optional app-specific dialogs / floating UI rendered after the main shell. */
  overlays?: ReactNode;
  /** Optional persistent right-side app panel, such as an assistant/context rail. */
  rightSidebar?: ReactNode;
  /**
   * Opt-in mobile treatment for the right-side app panel. On phones the panel
   * becomes a full-screen overlay reached from a header control instead of a
   * squeezed persistent column.
   */
  mobileRightSidebar?: boolean;
  /** Accessible label for the mobile right-panel control. */
  rightSidebarLabel?: string;
  /** Increment to programmatically reveal the compact right-panel overlay. */
  rightSidebarOpenToken?: number;
  /** Optional Sidebar footer override. */
  sidebarFooter?: ReactNode;
  /** Optional main workspace classes. Defaults preserve existing applications. */
  contentClassName?: string;
  children: ReactNode;
}

export function AppShell({
  branding,
  groups,
  head,
  decorate,
  collapsibleNavGroups,
  navGroupsInitiallyCollapsed,
  navGroupStorageKey,
  subNavigation,
  collapsibleSidebar,
  sidebarStorageKey,
  forceSidebarCollapsed,
  searchSlot,
  sidebarCommand,
  sidebarWorkspace,
  sidebarExtras,
  actions,
  headerVariant,
  overlays,
  rightSidebar,
  mobileRightSidebar = false,
  rightSidebarLabel = 'Assistant',
  rightSidebarOpenToken = 0,
  sidebarFooter,
  contentClassName,
  children,
}: AppShellProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const isCompact = useIsCompact();

  /*
   * The right panel is a third column, and three columns need real width.
   * Below `lg` the nav rail plus an assistant panel left the workspace around
   * 120px on a 768px tablet — narrower than the phone layout it was supposedly
   * an upgrade from. So the overlay treatment covers everything below `lg`, not
   * just phones, and the persistent column returns at `lg` where it fits.
   */
  const asOverlay = Boolean(rightSidebar) && mobileRightSidebar && isCompact;
  const closeRightPanel = useCallback(() => setRightPanelOpen(false), []);
  const shellPanel = useMemo(
    () => ({ inMobileOverlay: asOverlay, close: closeRightPanel }),
    [asOverlay, closeRightPanel],
  );

  useEffect(() => {
    if (rightSidebarOpenToken > 0 && asOverlay) setRightPanelOpen(true);
  }, [asOverlay, rightSidebarOpenToken]);

  return (
    <PageHeaderProvider fallback={head}>
      {(pageHeader) => (
        <TooltipProvider delayDuration={300}>
          <div className="flex h-app-viewport w-full overflow-hidden bg-background text-foreground">
            <Sidebar
              branding={branding}
              groups={groups}
              mobileOpen={mobileNavOpen}
              onMobileOpenChange={setMobileNavOpen}
              decorate={decorate}
              collapsibleGroups={collapsibleNavGroups}
              navGroupsInitiallyCollapsed={navGroupsInitiallyCollapsed}
              navGroupStorageKey={navGroupStorageKey}
              subNavigation={subNavigation}
              collapsibleSidebar={collapsibleSidebar}
              sidebarStorageKey={sidebarStorageKey}
              forceDesktopCollapsed={forceSidebarCollapsed}
              commandSlot={sidebarCommand}
              workspaceSlot={sidebarWorkspace}
              extrasSlot={sidebarExtras}
              footer={sidebarFooter}
            />
            <div className="flex min-w-0 flex-1">
              <div className="flex min-w-0 flex-1 flex-col">
                <Header
                  head={pageHeader}
                  onOpenMobileNav={() => setMobileNavOpen(true)}
                  searchSlot={searchSlot}
                  actions={actions}
                  variant={headerVariant}
                  panelToggle={
                    asOverlay ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 flex-shrink-0"
                        onClick={() => setRightPanelOpen(true)}
                        aria-label={`Open ${rightSidebarLabel}`}
                      >
                        <PanelRight className="h-5 w-5" aria-hidden />
                      </Button>
                    ) : undefined
                  }
                />
                {/*
                  `overscroll-contain`: reaching the end of the workspace must
                  not chain the scroll to the document behind it, which on a
                  phone shows up as rubber-banding or an accidental
                  pull-to-refresh mid-read.
                */}
                <main
                  className={cn(
                    'flex-1 overflow-y-auto overscroll-contain px-3 pt-3 pb-[max(4rem,calc(2.5rem+env(safe-area-inset-bottom)))] sm:px-5 sm:pt-4 sm:pb-[max(4rem,calc(2.5rem+env(safe-area-inset-bottom)))]',
                    contentClassName,
                  )}
                >
                  <ErrorBoundary title="This view crashed.">{children}</ErrorBoundary>
                  <PageEndMarker />
                </main>
              </div>
              {rightSidebar && !asOverlay && (
                <aside className="hidden min-h-0 flex-shrink-0 border-l border-border bg-card lg:flex">
                  <ShellPanelProvider value={shellPanel}>{rightSidebar}</ShellPanelProvider>
                </aside>
              )}
            </div>
          </div>
          {/*
            Mobile: the panel is a full-screen overlay above the shell. It is
            only mounted while open so a collapsed assistant never eats a strip
            of an already-narrow screen.
          */}
          {rightSidebar && asOverlay && rightPanelOpen && (
            <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
              <button
                type="button"
                className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
                onClick={closeRightPanel}
                aria-label={`Close ${rightSidebarLabel}`}
              />
              <aside className="absolute inset-y-0 right-0 flex h-app-viewport w-full flex-col border-l border-border bg-card shadow-2xl">
                <div className="flex items-center justify-end border-b border-border px-2 py-1.5 pt-[max(0.375rem,env(safe-area-inset-top))]">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={closeRightPanel}
                    aria-label={`Close ${rightSidebarLabel}`}
                  >
                    <X className="h-5 w-5" aria-hidden />
                  </Button>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <ShellPanelProvider value={shellPanel}>{rightSidebar}</ShellPanelProvider>
                </div>
              </aside>
            </div>
          )}
          {overlays}
        </TooltipProvider>
      )}
    </PageHeaderProvider>
  );
}

/**
 * A quiet "you have reached the bottom" rule, phones only.
 *
 * A page whose content fits the viewport cannot scroll, and a phone gives no
 * feedback for a swipe that has nowhere to go — no bounce, no movement. Several
 * short pages in the portal therefore read as frozen rather than complete, and
 * the natural response is to keep swiping at a page that is already finished.
 *
 * A visible end mark answers the question the missing scroll leaves open. It is
 * deliberately not a footer: nothing here is navigable, because a control at the
 * bottom of every page is a control people hit by accident while swiping.
 */
function PageEndMarker() {
  return (
    <div className="mt-8 flex items-center gap-3 pb-2 md:hidden" aria-hidden>
      <span className="h-px flex-1 bg-border" />
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">
        End of page
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
